<!--
  Универсальная пагинация со ссылками через query-параметр.

  На десктопе рендерит серию страниц (series), которую присылает бэкенд
  в pagination_meta (числа + служебное значение 'gap' для "…").

  На мобильных (<=767px) игнорирует пришедший series и строит свой
  укороченный ряд из page/pages (см. buildCompactSeries), чтобы не
  показывать слишком много кнопок подряд на узком экране: всегда первая
  и последняя страница + окно ближайших соседей вокруг текущей.
-->
<template>
  <div class="pagination" v-if="pages > 1">
    <router-link
      :to="getPageLink(page - 1)"
      custom
      v-slot="{ navigate }"
    >
      <button @click="navigate" :disabled="page == 1">Назад</button>
    </router-link>

    <template v-for="pageNumber in displaySeries" :key="pageNumber">
      <span v-if="pageNumber === 'gap'" class="pagination-gap">
        …
      </span>

      <router-link
        v-else
        :to="getPageLink(pageNumber)"
        custom
        v-slot="{ navigate }"
      >
        <button
          @click="navigate"
          :class="{ active: page == pageNumber }"
        >
          {{ pageNumber }}
        </button>
      </router-link>
    </template>

    <router-link
      :to="getPageLink(page + 1)"
      custom
      v-slot="{ navigate }"
    >
      <button @click="navigate" :disabled="page == pages">
        Вперед
      </button>
    </router-link>
  </div>
</template>

<script>
// Ширина экрана, ниже которой пагинация переключается на сжатый вид.
const MOBILE_BREAKPOINT_QUERY = "(max-width: 767px)";

export default {
  name: "PaginationComponent",
  props: {
    page: {
      type: Number,
      required: true,
    },
    pages: {
      type: Number,
      required: true,
    },
    series: {
      type: Array,
      required: true,
    },
    routeQuery: {
      type: Object,
      default: () => ({}),
    },
    pageParam: {
      type: String,
      default: "page",
    },
  },
  data() {
    return {
      isMobile: false,
      mediaQueryList: null,
    };
  },
  computed: {
    // На мобильном сервер может прислать слишком длинный series
    // (например 1 … 5 6 7 8 9 … 20). Строим свой укороченный ряд
    // из page/pages, не трогая проп series и логику родителя/бэкенда.
    // На десктопе рендерится ровно то, что пришло с сервера.
    displaySeries() {
      if (!this.isMobile) return this.series;
      return this.buildCompactSeries(this.page, this.pages);
    },
  },

  methods: {
    handleMediaChange(event) {
      this.isMobile = event.matches;
    },

    getPageLink(pageNumber) {
      return {
        query: {
          ...this.routeQuery,
          [this.pageParam]: pageNumber,
        },
      };
    },

    // Собирает короткий ряд вида 1 … (page-2)..(page+2) … pages,
    // показывая gap только если между соседними числами есть реальный разрыв (>1).
    buildCompactSeries(page, pages) {
      const result = [1];

      const windowStart = Math.max(page - 2, 2);
      const windowEnd = Math.min(page + 2, pages - 1);

      if (windowStart > 2) result.push("gap");

      for (let i = windowStart; i <= windowEnd; i++) {
        result.push(i);
      }

      if (windowEnd < pages - 1) result.push("gap");

      if (pages > 1) result.push(pages);

      return result;
    },
  },

  created() {
    this.mediaQueryList = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    this.isMobile = this.mediaQueryList.matches;
    this.mediaQueryList.addEventListener(
      "change",
      this.handleMediaChange
    );
  },

  unmounted() {
    this.mediaQueryList?.removeEventListener(
      "change",
      this.handleMediaChange
    );
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 5px;
}

.pagination button {
  padding: 5px 10px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  border-radius: 4px;
  min-width: 36px;
}

.pagination button:hover:not(:disabled) {
  border-color: var(--color-green);
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination button.active {
  background: var(--color-green);
  color: white;
  border-color: var(--color-green);
}

.pagination-gap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  color: #888;
  user-select: none;
}

@media screen and (max-width: 767px) {
  .pagination {
    flex-wrap: nowrap;
    justify-content: center;
    margin-top: 15px;
    gap: 4px;
  }

  .pagination button {
    flex: 0 0 auto;
    padding: 5px 8px;
    min-width: 32px;
    font-size: 13px;
  }

  .pagination-gap {
    flex: 0 0 auto;
    min-width: 20px;
  }
}
</style>
